import { z } from "zod";

import {
  CRYPTO_ORDER_TYPES,
  CRYPTO_QUANTITY_TYPES,
  CRYPTO_TIME_IN_FORCE,
  CRYPTO_TIME_IN_FORCE_OPTIONS,
  type CryptoOrderType,
  type CryptoTimeInForce,
} from "~/v2/constants/cryptoOrder";
import { isGTZero } from "./utils";

export const zCryptoOrderTicketSchema = z.object({
  orderType: z.enum(CRYPTO_ORDER_TYPES),
  timeInForce: z.enum(CRYPTO_TIME_IN_FORCE),
  quantityType: z.enum(CRYPTO_QUANTITY_TYPES),
  quantity: z.coerce.number(),
  limitPrice: z.coerce.number().optional(),
  stopPrice: z.coerce.number().optional(),
});

export type CryptoOrderTicketSchema = z.infer<typeof zCryptoOrderTicketSchema>;

export const isTimeInForceValid = ({
  timeInForce,
  orderType,
}: {
  timeInForce: CryptoTimeInForce;
  orderType: CryptoOrderType;
}) =>
  CRYPTO_TIME_IN_FORCE_OPTIONS.find(
    (option) => option.value === timeInForce,
  )?.orderTypes.includes(orderType) ?? false;

export const cryptoOrderTicketSchemaSuperRefinement = (
  {
    orderType,
    limitPrice,
    stopPrice,
    quantity,
    timeInForce,
  }: CryptoOrderTicketSchema,
  refinementContext: z.RefinementCtx,
) => {
  if (
    (orderType === "limit" || orderType === "stop_limit") &&
    !isGTZero(limitPrice)
  ) {
    refinementContext.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Limit Price must be greater than 0",
      path: ["limitPrice"],
    });
  }

  if (orderType === "stop_limit" && !isGTZero(stopPrice)) {
    refinementContext.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Stop Price must be greater than 0",
      path: ["stopPrice"],
    });
  }

  if (!isGTZero(quantity)) {
    refinementContext.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Quantity must be greater than 0",
      path: ["quantity"],
    });
  }

  if (!isTimeInForceValid({ timeInForce, orderType })) {
    refinementContext.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Time in force is not valid",
      path: ["timeInForce"],
    });
  }
};
